/**
 * notificationDeepLink — maps a system notification (``UserNotification``,
 * distinct from a "nudge") to the in-app surface it should open.
 *
 * Mirrors ``nudgeDeepLink.ts``: the BE owns ``action_url`` as a free-form
 * string, the FE maps only the paths it recognizes to a typed router
 * target and renders everything else as a plain, non-navigable row
 * (forward-safe for BE action_urls the FE doesn't model yet).
 */

import type { UserNotification } from "@ando/core";

export interface NotificationLinkTarget {
  readonly to: "/workspace/flows" | "/workspace/shares";
  /** Workspace URL contract (``sub`` lane, ``focus`` record) when the BE sends one. */
  readonly search?: Readonly<Record<string, string>>;
}

const SHARES_PATH = "/workspace/shares";

export function notificationDeepLink(
  notification: UserNotification,
): NotificationLinkTarget | null {
  const url = notification.action_url ?? "";
  if (url === "/workspace/flows") {
    return { to: "/workspace/flows" };
  }
  // ``share_received`` — a colleague shared an answer / a conversation. The BE
  // sends ``/workspace/shares?sub=received&focus=<share_id>``; the hub opens
  // the Ricevute lane with the drawer on that share.
  if (url === SHARES_PATH || url.startsWith(`${SHARES_PATH}?`)) {
    const query = url.slice(SHARES_PATH.length + 1);
    const search = Object.fromEntries(new URLSearchParams(query));
    return { to: SHARES_PATH, search: Object.keys(search).length > 0 ? search : undefined };
  }
  return null;
}
